import { XlsxColumn } from "@/lib/xlsx";
import { Group } from "@/models/feature/feature";
import { InvitedUser, User, UserWithRole } from "@/models/users/User";
import Workspace, { getAllWorkspaceDocuments } from "@/models/workspace/Workspace";

/** Joined inside a single cell. */
const LIST_SEPARATOR = ", ";

/** dd.mm.yyyy — zero-padded, so Excel reads it as a date and it sorts correctly. */
const formatDate = (value?: string | Date): string => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const fullName = (user?: { firstName?: string; lastName?: string }): string =>
  `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();

/** Maps each user id to the names of the groups the user belongs to. */
export const groupNamesByUserId = (groups: Group[]): Map<string, string[]> => {
  const byUserId = new Map<string, string[]>();

  for (const group of groups) {
    for (const userId of group.userIds) {
      const names = byUserId.get(userId);
      if (names) names.push(group.name);
      else byUserId.set(userId, [group.name]);
    }
  }

  return byUserId;
};

/* ─── Brugere ──────────────────────────────────────────────────────────────── */

export interface UserExportRow {
  user: UserWithRole;
  groupNames: string[];
}

/** Pairs each user with their group names, so the sheet can show both. */
export const toUserExportRows = (
  users: UserWithRole[],
  groups: Group[]
): UserExportRow[] => {
  const groupNames = groupNamesByUserId(groups);
  return users.map((user) => ({
    user,
    groupNames: groupNames.get(user.id) ?? [],
  }));
};

export const userExportColumns: XlsxColumn<UserExportRow>[] = [
  { header: "Fornavn", value: ({ user }) => user.firstName ?? "" },
  { header: "Efternavn", value: ({ user }) => user.lastName ?? "" },
  { header: "Email", value: ({ user }) => user.email ?? "" },
  {
    header: "Roller",
    value: ({ user }) =>
      user.roles.map((role) => role.displayName).join(LIST_SEPARATOR),
  },
  { header: "Grupper", value: ({ groupNames }) => groupNames.join(LIST_SEPARATOR) },
  { header: "Oprettet", value: ({ user }) => formatDate(user.createdAt) },
];

/* ─── Inviterede brugere ───────────────────────────────────────────────────── */

/** Mirrors the status shown in the table. */
const invitationStatus = (invitation: InvitedUser, now: Date): string =>
  now > new Date(invitation.expiresAt) ? "Udløbet" : "Afventer accept";

export const invitedUserExportColumns = (
  now: Date
): XlsxColumn<InvitedUser>[] => [
  { header: "Email", value: (invitation) => invitation.invitee.email ?? "" },
  { header: "Inviteret af", value: (invitation) => invitation.inviter.name ?? "" },
  { header: "Status", value: (invitation) => invitationStatus(invitation, now) },
  { header: "Inviteret", value: (invitation) => formatDate(invitation.createdAt) },
  { header: "Udløber", value: (invitation) => formatDate(invitation.expiresAt) },
  {
    header: "Roller",
    value: (invitation) =>
      (invitation.roles ?? [])
        .map((role) => role.displayName)
        .join(LIST_SEPARATOR),
  },
];

/* ─── Grupper ──────────────────────────────────────────────────────────────── */

export interface GroupMemberRow {
  groupName: string;
  memberCount: number;
  member?: User;
}

/**
 * Flattens groups to one row per membership, which is what makes the sheet useful for
 * working out who sits in which department. Empty groups keep a single row so they are
 * still visible.
 */
export const toGroupMemberRows = (
  groups: Group[],
  users: User[]
): GroupMemberRow[] => {
  const usersById = new Map(users.map((user) => [user.id, user]));

  return groups.flatMap((group) => {
    const members = group.userIds
      .map((userId) => usersById.get(userId))
      .filter((member): member is User => Boolean(member));

    if (members.length === 0)
      return [{ groupName: group.name, memberCount: 0 }];

    return members.map((member) => ({
      groupName: group.name,
      memberCount: members.length,
      member,
    }));
  });
};

export const groupExportColumns: XlsxColumn<GroupMemberRow>[] = [
  { header: "Gruppe", value: (row) => row.groupName },
  { header: "Antal medlemmer", value: (row) => String(row.memberCount) },
  { header: "Medlem", value: (row) => fullName(row.member) },
  { header: "Email", value: (row) => row.member?.email ?? "" },
];

/* ─── Forældreløse workspaces ──────────────────────────────────────────────── */

export const orphanedWorkspaceExportColumns: XlsxColumn<Workspace>[] = [
  { header: "Navn", value: (workspace) => workspace.name },
  { header: "Beskrivelse", value: (workspace) => workspace.description ?? "" },
  {
    header: "Dokumenter",
    value: (workspace) => String(getAllWorkspaceDocuments(workspace).length),
  },
  { header: "Oprettet", value: (workspace) => formatDate(workspace.createdAt) },
];
